import {useUserStore} from '@/store/user'
import {User} from 'oidc-client-ts'
import jwt_decode from 'jwt-decode'
import {getRegionForSalesArea} from '~/assets/js/utils/region_service'
import type {KeycloakTokenPayload} from './types'

export function decodeKeycloakToken(accessToken: string): KeycloakTokenPayload {
  return jwt_decode(accessToken) as KeycloakTokenPayload
}

// Per-tab impersonation token. Stored in sessionStorage (NOT localStorage) so it is scoped to a
// single browser tab: it survives navigation and refreshes within the impersonation tab, dies when
// that tab closes, and is never visible to the admin's other tabs (which share localStorage).
const IMPERSONATION_STORAGE_KEY = 'enext.keycloak.impersonation'

export function setImpersonationToken(accessToken: string) {
  if (typeof globalThis !== 'undefined' && globalThis.sessionStorage) {
    globalThis.sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, accessToken)
  }
}

export function getImpersonationToken(): string | null {
  if (typeof globalThis !== 'undefined' && globalThis.sessionStorage) {
    return globalThis.sessionStorage.getItem(IMPERSONATION_STORAGE_KEY)
  }
  return null
}

export function clearImpersonationToken() {
  if (typeof globalThis !== 'undefined' && globalThis.sessionStorage) {
    globalThis.sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY)
  }
}

export function isImpersonationTokenValid(accessToken: string): boolean {
  try {
    const decoded = decodeKeycloakToken(accessToken)
    return typeof decoded.exp === 'number' && decoded.exp * 1000 > Date.now()
  } catch {
    return false
  }
}

export function restoreUserStoreFromAccessToken(accessToken: string) {
  const userStore = useUserStore()
  const decodedToken = decodeKeycloakToken(accessToken)

  userStore.setAccessToken(accessToken)

  if (decodedToken.oid) {
    userStore.setUserUuid(decodedToken.oid)
  }
  if (decodedToken.name) {
    userStore.setUserName(decodedToken.name)
  }

  return decodedToken
}

export function buildKeycloakUser(accessToken: string, idToken: string | undefined, refreshToken: string | undefined, state: any) {
  const decodedToken = decodeKeycloakToken(accessToken)
  const subject = decodedToken.oid ?? ''

  return new User({
    id_token: idToken ?? '',
    access_token: accessToken,
    refresh_token: refreshToken ?? '',
    token_type: 'Bearer',
    scope: decodedToken.scope ?? 'openid profile email',
    profile: {
      sub: subject,
      name: decodedToken.name,
      oid: subject,
      ...decodedToken
    } as any,
    session_state: null,
    expires_at: decodedToken.exp ?? (Math.floor(Date.now() / 1000) + 3600),
    userState: state
  })
}

export function getTokenRegion(accessToken: string) {
  const decodedToken = decodeKeycloakToken(accessToken)
  return decodedToken.salesAreaName
    ? getRegionForSalesArea(decodedToken.salesAreaName.replaceAll('_', '-'))
    : null
}
